import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ObjectPageTabs.module.css';

export type ObjectPageTab = 'main' | 'feed' | 'management';

interface ObjectPageTabsProps {
  activeTab: ObjectPageTab;
  onTabChange: (tab: ObjectPageTab) => void;
  mainContent: React.ReactNode;
  feedContent: React.ReactNode;
  managementContent: React.ReactNode;
  /** Шапка + вкладки в одном sticky-блоке (страница объекта) */
  renderLayout?: (parts: { tabBar: React.ReactNode; panels: React.ReactNode }) => React.ReactNode;
}

const TAB_ORDER: ObjectPageTab[] = ['main', 'feed', 'management'];

const TAB_LABELS: Record<ObjectPageTab, string> = {
  main: 'Главная',
  feed: 'Лента',
  management: 'Управление',
};

const SWIPE_THRESHOLD_RATIO = 0.18;
const SWIPE_THRESHOLD_RATIO_TOUCH = 0.12;

type GestureAxis = 'horizontal' | 'vertical';
type GestureSource = 'touch' | 'mouse';

interface GestureState {
  source: GestureSource;
  pointerId: number;
  startX: number;
  startY: number;
  axis: GestureAxis | null;
  offsetRatio: number;
}

interface GestureListeners {
  move: (event: Event) => void;
  end: (event: Event) => void;
}

const shouldIgnoreTabSwipe = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return !!target.closest(
    'button, a, input, textarea, select, [data-no-tab-swipe], .horizontal-category-tabs'
  );
};

const resolveGestureAxis = (deltaX: number, deltaY: number, isTouch: boolean): GestureAxis | null => {
  const absX = Math.abs(deltaX);
  const absY = Math.abs(deltaY);

  if (absX < 4 && absY < 4) {
    return null;
  }

  if (isTouch) {
    if (absY >= 16 && absY > absX * 1.75) {
      return 'vertical';
    }
    if (absX >= 5 && absX >= absY * 0.55) {
      return 'horizontal';
    }
    if (absX >= 12 && absX > absY) {
      return 'horizontal';
    }
    if (absY >= 22 && absY > absX * 1.25) {
      return 'vertical';
    }
    return null;
  }

  if (absY > absX && absY >= 10) {
    return 'vertical';
  }
  if (absX >= 8 && absX >= absY) {
    return 'horizontal';
  }

  return null;
};

const ObjectPageTabs: React.FC<ObjectPageTabsProps> = ({
  activeTab,
  onTabChange,
  mainContent,
  feedContent,
  managementContent,
  renderLayout,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<GestureState | null>(null);
  const activeIndexRef = useRef(0);
  const onTabChangeRef = useRef(onTabChange);
  const touchListenersRef = useRef<GestureListeners | null>(null);
  const mouseListenersRef = useRef<GestureListeners | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isGesturePending, setIsGesturePending] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const activeIndex = TAB_ORDER.indexOf(activeTab);
  activeIndexRef.current = activeIndex;
  onTabChangeRef.current = onTabChange;

  const cleanupGesture = useCallback(() => {
    const touchListeners = touchListenersRef.current;
    if (touchListeners) {
      window.removeEventListener('touchmove', touchListeners.move);
      window.removeEventListener('touchend', touchListeners.end);
      window.removeEventListener('touchcancel', touchListeners.end);
    }

    const mouseListeners = mouseListenersRef.current;
    if (mouseListeners) {
      window.removeEventListener('pointermove', mouseListeners.move);
      window.removeEventListener('pointerup', mouseListeners.end);
      window.removeEventListener('pointercancel', mouseListeners.end);
    }

    const viewport = viewportRef.current;
    if (viewport) {
      viewport.style.touchAction = '';
    }

    gestureRef.current = null;
    setIsGesturePending(false);
    setIsDragging(false);
    setDragOffset(0);
  }, []);

  useEffect(() => {
    const setTabByIndex = (index: number) => {
      const nextTab = TAB_ORDER[index];
      if (nextTab) {
        onTabChangeRef.current(nextTab);
      }
    };

    const applyHorizontalDrag = (deltaX: number) => {
      const viewport = viewportRef.current;
      const gesture = gestureRef.current;
      if (!viewport || !gesture) {
        return;
      }

      const width = viewport.clientWidth || 1;
      let offset = deltaX;

      if (activeIndexRef.current === 0 && offset > 0) {
        offset *= 0.25;
      }
      if (activeIndexRef.current === TAB_ORDER.length - 1 && offset < 0) {
        offset *= 0.25;
      }

      gesture.offsetRatio = offset / width;
      setDragOffset(gesture.offsetRatio);
    };

    const handleMove = (clientX: number, clientY: number, event: Event) => {
      const gesture = gestureRef.current;
      if (!gesture) {
        return;
      }

      const deltaX = clientX - gesture.startX;
      const deltaY = clientY - gesture.startY;
      const isTouch = gesture.source === 'touch';

      if (!gesture.axis) {
        if (isTouch && event.cancelable) {
          event.preventDefault();
        }

        const axis = resolveGestureAxis(deltaX, deltaY, isTouch);
        if (!axis) {
          return;
        }

        if (axis === 'vertical') {
          cleanupGesture();
          return;
        }

        gesture.axis = 'horizontal';
        setIsGesturePending(false);
        setIsDragging(true);
      }

      if (gesture.axis !== 'horizontal') {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      applyHorizontalDrag(deltaX);
    };

    const finishGesture = () => {
      const gesture = gestureRef.current;
      if (!gesture) {
        return;
      }

      if (gesture.axis === 'horizontal') {
        const ratio = gesture.offsetRatio;
        const index = activeIndexRef.current;
        const threshold = gesture.source === 'touch'
          ? SWIPE_THRESHOLD_RATIO_TOUCH
          : SWIPE_THRESHOLD_RATIO;

        if (ratio <= -threshold && index < TAB_ORDER.length - 1) {
          setTabByIndex(index + 1);
        } else if (ratio >= threshold && index > 0) {
          setTabByIndex(index - 1);
        }
      }

      cleanupGesture();
    };

    const getTouchPoint = (event: TouchEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.source !== 'touch') {
        return null;
      }

      const touch = Array.from(event.changedTouches).find((t) => t.identifier === gesture.pointerId)
        ?? Array.from(event.touches).find((t) => t.identifier === gesture.pointerId);

      return touch ?? null;
    };

    const onTouchMove = (event: Event) => {
      const touchEvent = event as TouchEvent;
      const touch = getTouchPoint(touchEvent)
        ?? (touchEvent.touches.length === 1 ? touchEvent.touches[0] : null);

      if (!touch) {
        return;
      }

      handleMove(touch.clientX, touch.clientY, event);
    };

    const onTouchEnd = (event: Event) => {
      const touchEvent = event as TouchEvent;
      const gesture = gestureRef.current;
      if (!gesture || gesture.source !== 'touch') {
        return;
      }

      const touch = Array.from(touchEvent.changedTouches).find((t) => t.identifier === gesture.pointerId);
      if (!touch && touchEvent.touches.length > 0) {
        return;
      }

      finishGesture();
    };

    const onPointerMove = (event: Event) => {
      const pointerEvent = event as PointerEvent;
      const gesture = gestureRef.current;
      if (!gesture || gesture.source !== 'mouse' || pointerEvent.pointerId !== gesture.pointerId) {
        return;
      }

      handleMove(pointerEvent.clientX, pointerEvent.clientY, event);
    };

    const onPointerEnd = (event: Event) => {
      const pointerEvent = event as PointerEvent;
      const gesture = gestureRef.current;
      if (!gesture || gesture.source !== 'mouse' || pointerEvent.pointerId !== gesture.pointerId) {
        return;
      }

      finishGesture();
    };

    touchListenersRef.current = { move: onTouchMove, end: onTouchEnd };
    mouseListenersRef.current = { move: onPointerMove, end: onPointerEnd };

    return () => {
      cleanupGesture();
    };
  }, [cleanupGesture]);

  const startGesture = useCallback((
    source: GestureSource,
    pointerId: number,
    clientX: number,
    clientY: number,
  ) => {
    if (gestureRef.current) {
      cleanupGesture();
    }

    gestureRef.current = {
      source,
      pointerId,
      startX: clientX,
      startY: clientY,
      axis: null,
      offsetRatio: 0,
    };

    const viewport = viewportRef.current;
    if (viewport) {
      viewport.style.touchAction = 'none';
    }

    setIsGesturePending(true);
    setDragOffset(0);

    if (source === 'touch') {
      const listeners = touchListenersRef.current;
      if (!listeners) {
        return;
      }
      window.addEventListener('touchmove', listeners.move, { passive: false });
      window.addEventListener('touchend', listeners.end);
      window.addEventListener('touchcancel', listeners.end);
      return;
    }

    const listeners = mouseListenersRef.current;
    if (!listeners) {
      return;
    }
    window.addEventListener('pointermove', listeners.move, { passive: false });
    window.addEventListener('pointerup', listeners.end);
    window.addEventListener('pointercancel', listeners.end);
  }, [cleanupGesture]);

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) {
      return;
    }

    if (shouldIgnoreTabSwipe(event.target)) {
      return;
    }

    const touch = event.touches[0];
    startGesture('touch', touch.identifier, touch.clientX, touch.clientY);
  }, [startGesture]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch') {
      return;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    if (shouldIgnoreTabSwipe(event.target)) {
      return;
    }

    startGesture('mouse', event.pointerId, event.clientX, event.clientY);
  }, [startGesture]);

  const trackTransform = `translateX(calc(-${activeIndex * 100}% + ${dragOffset * 100}%))`;
  const indicatorTransform = `translateX(${(activeIndex - dragOffset) * 100}%)`;

  const tabBar = (
    <div className={styles.tabsBar} role="tablist" aria-label="Разделы объекта">
      {TAB_ORDER.map((tab) => {
        const isActive = tab === activeTab;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ''}`}
            onClick={() => onTabChange(tab)}
          >
            {TAB_LABELS[tab]}
          </button>
        );
      })}
      <span
        className={`${styles.tabIndicator} ${isDragging ? styles.tabIndicatorDragging : ''}`}
        style={{
          width: `${100 / TAB_ORDER.length}%`,
          transform: indicatorTransform,
        }}
        aria-hidden
      />
    </div>
  );

  const panels = (
    <div
      ref={viewportRef}
      className={`${styles.viewport} ${isGesturePending ? styles.viewportBlocking : ''} ${isDragging ? styles.viewportDragging : ''}`}
      onTouchStart={handleTouchStart}
      onPointerDown={handlePointerDown}
    >
      <div
        className={styles.track}
        style={{
          transform: trackTransform,
          transition: isDragging ? 'none' : 'transform 0.25s ease',
        }}
      >
        <div
          className={styles.tabPanel}
          role="tabpanel"
          aria-hidden={activeTab !== 'main'}
        >
          {mainContent}
        </div>
        <div
          className={styles.tabPanel}
          role="tabpanel"
          aria-hidden={activeTab !== 'feed'}
        >
          {feedContent}
        </div>
        <div
          className={styles.tabPanel}
          role="tabpanel"
          aria-hidden={activeTab !== 'management'}
        >
          {managementContent}
        </div>
      </div>
    </div>
  );

  if (renderLayout) {
    return <>{renderLayout({ tabBar, panels })}</>;
  }

  return (
    <div className={styles.tabsRoot}>
      {tabBar}
      {panels}
    </div>
  );
};

export default ObjectPageTabs;
